import { sound } from "@pixi/sound";
import { SoundDto } from "./SoundDto";
import { SoundData } from "./SoundData";
import { isMobile } from "@pixi/core";
import { AudioContextManager } from "../scenes/common/AudioContextManager";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { IGameDistributionSdk } from "../gamedistribution/interfaces/IGameDistributionSdk";
import { InjectableInterfaces } from "../composition/InjectableInterfaces";

@injectable()
export class AudioManager {

    private _isPlaySounds: boolean;
    public get isPlaySounds(): boolean {
        return this._isPlaySounds;
    }
    public set isPlaySounds(value: boolean) {
        this._isPlaySounds = value;
        localStorage.setItem(AudioManager.SOUNDS_STORAGE_KEY, (value ? 1 : 0).toString());
        this.setSoundsVolume(value);
    }

    private _isPlayMusic: boolean;
    public get isPlayMusic(): boolean {
        return this._isPlayMusic;
    }
    public set isPlayMusic(value: boolean) {
        this._isPlayMusic = value;
        localStorage.setItem(AudioManager.MUSIC_STORAGE_KEY, (value ? 1 : 0).toString());
        this.setMusicVolume(value);
    }

    private get soundData(): Array<SoundDto> {
        return this.soundConfig.soundParams;
    }
    private soundConfig: SoundData;
    private currentMusic: string;
    private boundVisibilityHandler: () => void;
    private readonly audioContextManager: AudioContextManager;

    private static readonly SOUNDS_STORAGE_KEY = "play_sounds";
    private static readonly MUSIC_STORAGE_KEY = "play_music";

    public constructor(@inject(InjectableInterfaces.IGameDistributionSdk) private readonly gdSdk: IGameDistributionSdk) {
        this.soundConfig = SoundData.fromJson();
        sound.add(this.soundConfig.soundsManifest, { singleInstance: true });
        const playSounds = localStorage.getItem(AudioManager.SOUNDS_STORAGE_KEY);
        const playMusic = localStorage.getItem(AudioManager.MUSIC_STORAGE_KEY);
        this.isPlaySounds = playSounds ? Boolean(+playSounds) : true;
        this.isPlayMusic = playMusic ? Boolean(+playMusic) : true;
        this.boundVisibilityHandler = this.onVisibilityChange.bind(this);
        document.addEventListener("visibilitychange", this.boundVisibilityHandler);
        sound.context.audioContext.addEventListener("statechange", this.onAudioContextStateChange.bind(this), { once: true });
        this.audioContextManager = new AudioContextManager(sound.context.audioContext);
        this.audioContextManager.tryResume();
        this.gdSdk.pauseAudio.add(this.pauseAudioForAd, this);
        this.gdSdk.resumeAudio.add(this.resumeAudioForAd, this);
    }

    public play(alias: string): void {
        if (sound.exists(alias)) {
            const soundConfig = this.soundData.find((data) => data.alias === alias);
            if (soundConfig.isMusic) {
                this.currentMusic = alias;
            }
            if (isMobile.apple.phone || sound.context.audioContext.state === "running") {
                sound.play(alias, { loop: soundConfig.isMusic, volume: soundConfig.volume, singleInstance: soundConfig.isMusic });
            }
        }
    }

    public stop(alias: string): void {
        if (sound.exists(alias)) {
            sound.stop(alias);
        }
    }

    private setSoundsVolume(state: boolean): void {
        this.soundData.forEach(soundConfig => {
            if (!soundConfig.isMusic) {
                sound.volume(soundConfig.alias, state ? soundConfig.volume : 0);
            }
        });
    };

    private setMusicVolume(state: boolean): void {
        this.soundData.forEach(soundConfig => {
            if (soundConfig.isMusic) {
                sound.volume(soundConfig.alias, state ? soundConfig.volume : 0);
            }
        });
    };

    private async onVisibilityChange(): Promise<void> {
        if (!document.hidden) {
            await this.audioContextManager.tryResume();
        }
        this._isPlaySounds && this.setSoundsVolume(!document.hidden);
        this._isPlayMusic && this.setMusicVolume(!document.hidden);
    }

    private onAudioContextStateChange(): void {
        switch (sound.context.audioContext.state) {
            case "closed":
            case "suspended": {
                sound.stopAll();
                break;
            }
            case "running": {
                this.play(this.currentMusic);
                break;
            }
        }
    }

    private pauseAudioForAd(): void {
        this.setSoundsVolume(false);
        this.setMusicVolume(false);
    }

    private resumeAudioForAd(): void {
        this._isPlaySounds && this.setSoundsVolume(true);
        this._isPlayMusic && this.setMusicVolume(true);
    }
}